import { npcPolicy, type NpcTemperament } from "../npcs/policy.ts";
import { ZoneRule, flagStateOf } from "../zones/zones.ts";

/**
 * Who may fight whom (ADR-073 decision 6, with the zone rule bits of ADR-054's amendment of 2026-09-27,
 * decision 8): one pure function over plain facts the caller supplies, the map server deciding with it and the
 * page asking it only to grey what cannot land. The caller excludes self and the dead before asking.
 */

const FLAGGED = 1;
const COMBATANT = 2;

/**
 * What the rule reads of one side: whether it is a character, a creature or an NPC (ADR-136; an NPC's `temperament` decides
 * who may harm it and whom it may harm, and an NPC with none is read as passive, so a forgotten one harms nobody), its PvP byte as the snapshot carries it
 * (the switching bit and the Hostile lock bit are masked off here), the rule bits of the cell it stands on (`ZoneRule`, 0 on open ground),
 * its duel's number, 0 outside one, and whether a Game Master has frozen it (`held`, ADR-056's amendment of
 * 2026-10-06, decision 38), which a page reads from the away state the snapshot carries (`Away.Held`) and the map
 * server from its own record, its party's id (0 for none; two sides share a party when both hold the same non-zero id,
 * which only `mayHelp` reads) and its PvP preference (0 Protected, 1 Flag on; only a helper's is read, and a creature's
 * is 0).
 */
export interface Fighter {
  readonly kind: "character" | "creature" | "npc";
  readonly temperament?: NpcTemperament;
  readonly flag: number;
  readonly zone: number;
  readonly duel: number;
  readonly held: boolean;
  readonly party: number;
  readonly preference: number;
}

/**
 * Whether one fighter may harm another. A held side closes every case before any other rule, a duel's included
 * (ADR-073's note of 2026-10-06): a held character neither harms nor is harmed. A duellist harms and is harmed by its opponent only, so creatures leave
 * duellists alone; two characters fight when both are open to player combat (`pvpOpen`), so a Sanctuary shelters a
 * Flagged character and never a Combatant, who stays open and can hit back until its window ends (ADR-073's
 * amendment of 2026-09-27); a character and a creature may, either way, unless either stands where creatures are
 * barred (`NoCreatures`: a Sanctuary or PvP only), so no blow crosses such an edge from either side; two creatures
 * never do. An NPC (ADR-136 decision 3) is harmed by a character unless it is passive, and by a creature only when it is
 * combative; it harms a character unless it is passive and a creature only when it is combative; two NPCs never harm each
 * other; and the same `NoCreatures` zones bar every blow between an NPC and a character or creature. Outside a duel, two characters of one party, and two in truce after a parting, never harm each other
 * (ADR-101 decisions 10 and 27); a duel the two fight against each other is decided first and wins over both.
 *
 * @param attacker The side dealing the harm.
 * @param target The side taking it.
 * @param truce Whether the map server's truce table holds the pair, in either direction, at this tick. Required: a caller that has no table, a creature's or a page's, says false, so a forgotten one is a compile error and never a fail-open default.
 * @returns True when the harm may land.
 * @example
 * mayHarm({ kind: "character", flag: 1, zone: 0, duel: 0, held: false, party: 0, preference: 0 }, { kind: "character", flag: 0, zone: 0, duel: 0, held: false, party: 0, preference: 0 }, false);
 * // false: the target is Protected
 */
export function mayHarm(attacker: Fighter, target: Fighter, truce: boolean): boolean {
  if (attacker.held || target.held) {
    return false;
  }
  if (attacker.duel !== 0 || target.duel !== 0) {
    return attacker.duel !== 0 && attacker.duel === target.duel;
  }
  if (attacker.kind === "character" && target.kind === "character") {
    if (truce || sharesParty(attacker, target)) {
      return false;
    }
    return pvpOpen(attacker) && pvpOpen(target);
  }
  return nonCharacterMayFight(attacker, target) && ((attacker.zone | target.zone) & ZoneRule.NoCreatures) === 0;
}

/**
 * Whether a blow between two sides of which at least one is not a character is allowed before the zone rule: a character and a creature
 * always; two creatures or two NPCs never; an NPC and a character by the NPC's temperament (`npcPolicy`); an NPC and a
 * creature only when the NPC is combative.
 *
 * @param attacker The side dealing the harm.
 * @param target The side taking it.
 * @returns True when the kinds and the temperament allow it.
 */
function nonCharacterMayFight(attacker: Fighter, target: Fighter): boolean {
  if (attacker.kind === target.kind) {
    return false;
  }
  if (attacker.kind === "npc") {
    const temperament = attacker.temperament ?? "passive";
    return target.kind === "character" ? npcPolicy(temperament).fights : temperament === "combative";
  }
  if (target.kind === "npc") {
    const policy = npcPolicy(target.temperament ?? "passive");
    return attacker.kind === "character" ? policy.playersMayAttack : policy.creaturesMayAttack;
  }
  return true;
}

/**
 * Whether a character is open to player combat: Combatant anywhere, or Flagged outside a cell that bars PvP. This is
 * what "Flagged" means to `mayHelp` (ADR-076's amendment of 2026-10-08, D1): a character sheltered in a Sanctuary is not.
 *
 * @param fighter The character.
 * @returns True when open.
 */
export function pvpOpen(fighter: Fighter): boolean {
  const state = flagStateOf(fighter.flag);
  return state === COMBATANT || (state === FLAGGED && (fighter.zone & ZoneRule.NoPvp) === 0);
}

/**
 * Whether two sides are members of one party: the same id, and not the id of no party.
 *
 * @param a One side.
 * @param b The other.
 * @returns True when both hold the same non-zero party id.
 */
export function sharesParty(a: Fighter, b: Fighter): boolean {
  return a.party !== 0 && a.party === b.party;
}
